/**
 * HeightSafetyTypePicker · iter303ak-fix26 — Quote Sent · T25 · A10
 * ────────────────────────────────────────────────────────────────
 * Multi-checkbox picker for the height-safety type(s) this job needs:
 *   • Scaffold
 *   • Edge Protection
 *   • Anchor & Rope
 * Combinations allowed (most 2-storey jobs need Scaffold + Edge).
 * Writes to `height_safety_types` = ["scaffold", "edge", "anchor_rope"].
 */
import React, { useState, useEffect } from 'react';
import { HardHat, CheckCircle2 } from 'lucide-react';

const OPTIONS = [
  { key: 'scaffold',    label: 'Scaffold',        blurb: 'Full frame scaffold — required for most 2-storey roofs.' },
  { key: 'edge',        label: 'Edge Protection', blurb: 'Perimeter rail / mesh — enough on many 1-storey jobs.' },
  { key: 'anchor_rope', label: 'Anchor & Rope',   blurb: 'Rope + anchor kit hire — for pitched work with fall arrest.' },
  { key: 'none',        label: 'No Safety Cost',  blurb: 'No height-safety charge on this quote — skips the costing and supplier steps.' },
];

export default function HeightSafetyTypePicker({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const [selected, setSelected] = useState(() => new Set(Array.isArray(value) ? value : (value?.types || [])));

  const toggle = (key) => {
    setSelected(prev => {
      if (prev.has(key)) { const next = new Set(prev); next.delete(key); return next; }
      // "No Safety Cost" is exclusive of the paid types.
      if (key === 'none') return new Set(['none']);
      const next = new Set(prev);
      next.delete('none');
      next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (onChange) onChange(Array.from(selected));
  }, [selected, onChange]);

  const valid = selected.size > 0;

  return (
    <div className="space-y-4" data-testid="wf-height-safety-type-picker">
      <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
        <div className="flex items-start gap-3">
          <HardHat className="w-5 h-5 mt-0.5 shrink-0 text-orange-700" />
          <div>
            <div className="text-sm font-semibold text-orange-900">Height Safety Type(s) Required</div>
            <div className="text-sm mt-1 text-orange-800">
              {params.prompt || 'Pick every height-safety type this job needs. Multi-select — 2-storey jobs usually need Scaffold + Edge.'}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {OPTIONS.map(opt => {
          const isOn = selected.has(opt.key);
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => toggle(opt.key)}
              data-testid={`wf-hs-type-${opt.key}`}
              className={`p-3 border rounded-lg text-left transition ${
                isOn ? 'border-orange-500 bg-orange-100 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(opt.key)}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded"
                />
                <span className={`text-sm font-semibold ${isOn ? 'text-orange-900' : 'text-slate-800'}`}>{opt.label}</span>
              </div>
              <div className={`text-[11px] mt-1.5 ${isOn ? 'text-orange-800' : 'text-slate-500'}`}>{opt.blurb}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-type-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-type-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!valid}
            className="px-4 py-1.5 rounded-md text-sm bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-hs-type-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
